import type { Theme } from '@/types';

export type ResolvedTheme = 'light' | 'dark';

const LEGACY_THEME_KEY = 'khiimori-theme';
const STORAGE_KEY = 'khiimori-storage';

export function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') {
    if (typeof window === 'undefined') return 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
}

export function applyThemeToDocument(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(resolved);
  root.style.colorScheme = resolved;
}

export function readThemeFromStorage(): Theme | null {
  if (typeof localStorage === 'undefined') return null;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const theme = parsed?.state?.theme;
      if (theme === 'light' || theme === 'dark' || theme === 'system') {
        return theme;
      }
    }
  } catch {
    // ignore parse errors
  }

  const legacy = localStorage.getItem(LEGACY_THEME_KEY);
  if (legacy === 'light' || legacy === 'dark' || legacy === 'system') {
    return legacy;
  }

  return null;
}

export function migrateLegacyTheme(): Theme | null {
  if (typeof localStorage === 'undefined') return null;
  const legacy = localStorage.getItem(LEGACY_THEME_KEY);
  if (legacy === 'light' || legacy === 'dark' || legacy === 'system') {
    localStorage.removeItem(LEGACY_THEME_KEY);
    return legacy;
  }
  return null;
}

export function getInitialTheme(): Theme {
  const stored = readThemeFromStorage();
  if (stored) return stored;
  const migrated = migrateLegacyTheme();
  if (migrated) return migrated;
  return 'dark';
}

let systemMediaQuery: MediaQueryList | null = null;
let systemListener: ((e: MediaQueryListEvent) => void) | null = null;

export function subscribeToSystemTheme(
  theme: Theme,
  onChange: (resolved: ResolvedTheme) => void
): () => void {
  if (typeof window === 'undefined' || theme !== 'system') {
    return () => {};
  }

  systemMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  systemListener = () => onChange(resolveTheme('system'));
  systemMediaQuery.addEventListener('change', systemListener);
  return () => {
    if (systemMediaQuery && systemListener) {
      systemMediaQuery.removeEventListener('change', systemListener);
    }
    systemMediaQuery = null;
    systemListener = null;
  };
}

export function initTheme(): ResolvedTheme {
  const theme = getInitialTheme();
  const resolved = resolveTheme(theme);
  applyThemeToDocument(resolved);
  return resolved;
}

export function toggleThemeValue(current: Theme): Theme {
  const resolved = resolveTheme(current);
  return resolved === 'dark' ? 'light' : 'dark';
}

